import posthog from 'posthog-js';
import { getSupabaseBrowserClient } from '../../lib/supabase';
import { sha256Bytes } from '../../lib/sha256';
import { lectureFileError, normalizeClozeText } from '../../lib/review-validation';
import type { ReportExportProgress } from '../../lib/export-progress';

export const API_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8000';
const API_KEY = process.env.NEXT_PUBLIC_STA_API_KEY || '';
export const MOCK_GENERATION_ENABLED = process.env.NEXT_PUBLIC_MOCK_GENERATION === 'true';
export const MOCK_GENERATION_DELAY_MS = Math.max(
  600,
  Number(process.env.NEXT_PUBLIC_MOCK_GENERATION_DELAY_MS || 2_500),
);

/** Error caused by user action (bad file type, file too large, etc.) — not a system failure. */
export class UserError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'UserError';
  }
}

/** Internal control-flow error used when a user explicitly stops generation. */
export class GenerationCancelledError extends Error {
  constructor() {
    super('Generation cancelled');
    this.name = 'GenerationCancelledError';
  }
}

// Cold start configuration
const COLD_START_TIMEOUT_MS = 90_000; // 90s max wait for cold start
const UPLOAD_TIMEOUT_MS = 120_000; // 120s timeout for upload (includes cold start buffer)
const WARMUP_POLL_INTERVAL_MS = 2_000; // Poll every 2s during warmup
const MAX_UPLOAD_RETRIES = 3; // Total retry attempts for upload
const RETRY_DELAYS_MS = [2_000, 4_000, 8_000]; // Exponential backoff delays
const EXPORT_TIMEOUT_MS = 120_000; // 120s timeout for export
const MAX_EXPORT_RETRIES = 2; // Total retry attempts for export
const REVIEW_READ_TIMEOUT_MS = 10_000;
const REVIEW_READ_RETRY_DELAYS_MS = [750, 1_500];
const FILE_READ_RETRY_DELAYS_MS = [150, 500];

export type ReviewUploadInitStage = 'warming_api' | 'reading_file' | 'initializing_upload';

// Track warm-up state
let warmUpPromise: Promise<WarmUpResult> | null = null;
let lastWarmUpTime = 0;
const WARMUP_CACHE_MS = 5 * 60 * 1000; // Consider API warm for 5 minutes

interface WarmUpResult {
  success: boolean;
  durationMs: number;
  attempts: number;
  wasColdStart: boolean;
}

/**
 * Ping the API health endpoint to wake up the Railway container.
 * Returns a promise that resolves when the API is ready.
 * Uses aggressive polling with timeout for cold start scenarios.
 */
export async function warmUpApi(): Promise<WarmUpResult> {
  // If we warmed up recently, skip
  if (Date.now() - lastWarmUpTime < WARMUP_CACHE_MS) {
    console.log('[API] Container recently warm, skipping warmup');
    return { success: true, durationMs: 0, attempts: 0, wasColdStart: false };
  }

  // Reuse existing warmup if in progress
  if (warmUpPromise) {
    console.log('[API] Warmup already in progress, waiting...');
    return warmUpPromise;
  }

  warmUpPromise = doWarmUp();
  const result = await warmUpPromise;
  warmUpPromise = null;

  if (result.success) {
    lastWarmUpTime = Date.now();
  }

  return result;
}

async function doWarmUp(): Promise<WarmUpResult> {
  const startTime = Date.now();
  let attempts = 0;
  const deadline = startTime + COLD_START_TIMEOUT_MS;

  console.log('[API] Starting container warmup...');

  while (Date.now() < deadline) {
    attempts++;
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10_000); // 10s per attempt

      const res = await fetch(`${API_URL}/api/health`, {
        method: 'GET',
        signal: controller.signal,
      });
      clearTimeout(timeoutId);

      if (res.ok) {
        const durationMs = Date.now() - startTime;
        const wasColdStart = durationMs > 5_000 || attempts > 1;

        console.log(`[API] Container ready after ${durationMs}ms (${attempts} attempts, coldStart=${wasColdStart})`);

        // Log metrics for cold start tracking
        logColdStartMetric({
          event: 'warmup_complete',
          durationMs,
          attempts,
          wasColdStart,
        });

        return { success: true, durationMs, attempts, wasColdStart };
      }
    } catch (err) {
      const elapsed = Date.now() - startTime;
      console.log(`[API] Warmup attempt ${attempts} failed after ${elapsed}ms:`, err instanceof Error ? err.message : err);
    }

    // Wait before next attempt
    if (Date.now() < deadline) {
      await new Promise(resolve => setTimeout(resolve, WARMUP_POLL_INTERVAL_MS));
    }
  }

  const durationMs = Date.now() - startTime;
  console.error(`[API] Warmup failed after ${durationMs}ms (${attempts} attempts)`);

  logColdStartMetric({
    event: 'warmup_failed',
    durationMs,
    attempts,
    wasColdStart: true,
  });

  return { success: false, durationMs, attempts, wasColdStart: true };
}

/**
 * Log cold start metrics to PostHog and console
 */
function logColdStartMetric(data: {
  event: string;
  durationMs: number;
  attempts: number;
  wasColdStart: boolean;
  error?: string;
}) {
  // Log to console for debugging
  console.log(`[ColdStart] ${data.event}:`, data);

  // Use the initialized module singleton. Module-based PostHog setups do not
  // reliably expose window.posthog, which caused these diagnostics to vanish.
  if (typeof window !== 'undefined') {
    posthog.capture(`api_${data.event}`, {
      duration_ms: data.durationMs,
      attempts: data.attempts,
      was_cold_start: data.wasColdStart,
      error: data.error,
    });
  }
}

/**
 * Ensure API is warm before proceeding. Call this before upload.
 * Shows user feedback via callback if cold start is detected.
 */
export async function ensureApiReady(
  onColdStartDetected?: () => void
): Promise<WarmUpResult> {
  const result = await warmUpApi();

  if (result.wasColdStart && onColdStartDetected) {
    onColdStartDetected();
  }

  return result;
}

async function authHeaders(extra?: Record<string, string>): Promise<Record<string, string>> {
  const headers: Record<string, string> = { ...extra };
  if (API_KEY) {
    headers['X-API-Key'] = API_KEY;
  }
  if (typeof window !== 'undefined') {
    const deviceId = localStorage.getItem('slidetoanki_device_id');
    if (deviceId) {
      headers['X-Device-ID'] = deviceId;
    }
  }
  const supabase = getSupabaseBrowserClient();
  const session = supabase ? (await supabase.auth.getSession()).data.session : null;
  if (!session?.access_token || session.user.is_anonymous) throw new UserError('Sign in to use SlideToAnki.');
  headers.Authorization = `Bearer ${session.access_token}`;
  return headers;
}

export interface SlideContent {
  slide_number: number;
  text: string;
  images: string[];
}

export interface UploadResponse {
  filename: string;
  num_slides: number;
  slides: SlideContent[];
  full_text: string;
  session_id: string | null;
}

export interface FlashCard {
  id: string;
  type: 'cloze' | 'basic' | 'image_occlusion';
  front?: string;
  back?: string;
  clozeText?: string;
  tags: string[];
  liked?: boolean;
  image?: string | null;
  // Image occlusion fields
  occludedImage?: string | null;
  answer?: string | null;
  hint?: string | null;
  occlusion?: import('../../lib/occlusion').Occlusion;
}

export interface GenerateResponse {
  cards: FlashCard[];
  model_used: string;
}

// ── Durable Review Queue v1 ────────────────────────────────────────────────

export type ReviewJobStatus =
  | 'queued'
  | 'parsing'
  | 'retrieving'
  | 'generating'
  | 'paused_buffer_full'
  | 'paused_user'
  | 'completing'
  | 'completed'
  | 'cancelled'
  | 'failed_retryable'
  | 'failed_terminal';

export interface ReviewCandidateCounts {
  pending: number;
  accepted: number;
  rejected: number;
  incorrect: number;
  reviewed: number;
  total: number;
}

export interface ReviewGenerationJob {
  id: string;
  upload_id: string;
  status: ReviewJobStatus;
  stage: string;
  requested_depth: string;
  requested_card_types: string[];
  accepted_goal: number;
  course_id?: string | null;
  preference_snapshot?: Record<string, unknown>;
  generator_version?: string | null;
  prompt_version?: string | null;
  last_error_code?: string | null;
  last_error_message?: string | null;
  created_at: string;
  started_at?: string | null;
  text_parse_completed_at?: string | null;
  media_parse_completed_at?: string | null;
  planning_completed_at?: string | null;
  first_candidate_ready_at?: string | null;
  source_slide_count?: number | null;
  source_excerpt?: { slide_number: number; text: string } | null;
  source_excerpts?: { slide_number: number; text: string }[];
  selected_facts?: { slide_number: number; text: string }[];
  card_estimate?: {
    minimum: number; maximum: number; planning_complete: boolean;
    planned_text_cards: number; planned_slides: number; total_slides: number;
  } | null;
  completed_at?: string | null;
  counts: ReviewCandidateCounts;
}

export interface ReviewCardVersion {
  id: string;
  candidate_id: string;
  version_number: number;
  created_by: string;
  front?: string | null;
  back?: string | null;
  cloze_text?: string | null;
  image_ref?: string | null;
  occluded_image_ref?: string | null;
  answer?: string | null;
  hint?: string | null;
  tags: string[];
}

export interface ReviewCandidate {
  id: string;
  job_id: string;
  source_chunk_id?: string | null;
  canonical_card_id?: string | null;
  origin: 'generated' | 'retrieved_exact' | 'retrieved_semantic' | 'adapted_reuse' | 'legacy_import';
  status: 'pending' | 'accepted' | 'rejected' | 'incorrect' | 'superseded';
  card_type: FlashCard['type'];
  ordinal: number;
  presented_at?: string | null;
  current_version_id: string;
  content_hash?: string | null;
  grounding_score?: number | null;
  retrieval_score?: number | null;
  quality_score_at_selection?: number | null;
  model_provider?: string | null;
  model_name?: string | null;
  prompt_version?: string | null;
  source?: {
    slide_number: number;
    excerpt: string;
  } | null;
  current_version: ReviewCardVersion;
}

export interface ReviewCoverageSlide {
  slide_number: number;
  excerpt: string;
  planned_facts: number;
  candidates: number;
  accepted: number;
  reviewed: number;
}

export interface ReviewCoverage {
  total_slides: number;
  content_slides: number;
  represented_slides: number;
  planned_facts: number;
  candidates: number;
  accepted: number;
  coverage_percent: number;
  slides: ReviewCoverageSlide[];
}

export interface CardPreferences {
  preferred_depth?: 'concise' | 'balanced' | 'thorough' | null;
  preferred_card_types: FlashCard['type'][];
  decision_count: number;
  accept_rate?: number | null;
  edit_rate?: number | null;
  incorrect_rate?: number | null;
  preferred_generated_type?: FlashCard['type'] | null;
  guidance: string[];
}

export interface CourseLecture {
  id: string;
  upload_id: string;
  job_id: string;
  title: string;
  position: number;
  status: ReviewJobStatus;
  stage: string;
  accepted: number;
  reviewed: number;
  total: number;
  created_at: string;
}

export interface StudyCourse {
  id: string;
  name: string;
  created_at: string;
  updated_at: string;
  lectures: CourseLecture[];
}

export type RejectionReason =
  | 'irrelevant'
  | 'duplicate'
  | 'wording'
  | 'too_detailed'
  | 'too_basic'
  | 'incorrect'
  | 'low_yield'
  | 'image_mask'
  | 'other';

export interface ReviewQueueSnapshot {
  job: ReviewGenerationJob;
  candidates: ReviewCandidate[];
}

export interface ReviewDecision {
  id: string;
  candidate_id: string;
  selected_version_id?: string | null;
  action: 'accept' | 'edit_accept' | 'reject' | 'incorrect';
  rejection_reason?: RejectionReason | null;
  reviewed_at: string;
  undone_at?: string | null;
}

async function reviewHeaders(): Promise<Record<string, string>> {
  return authHeaders({
    'Content-Type': 'application/json',
  });
}

/** Turn FastAPI/Pydantic structured error details into a useful user-safe message. */
export function formatApiErrorDetail(detail: unknown, fallback: string): string {
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (Array.isArray(detail)) {
    const messages = detail
      .map(item => formatApiErrorDetail(item, ''))
      .filter(Boolean);
    return messages.length ? messages.join('; ') : fallback;
  }
  if (detail && typeof detail === 'object') {
    const record = detail as Record<string, unknown>;
    for (const key of ['msg', 'message', 'detail', 'error']) {
      if (record[key] !== undefined) {
        const message = formatApiErrorDetail(record[key], '');
        if (message) return message.replace(/^Value error,\s*/i, '');
      }
    }
  }
  return fallback;
}

async function parseReviewResponse<T>(response: Response): Promise<T> {
  if (response.ok) return response.json();
  const body = await response.json().catch(() => ({ detail: 'Request failed' }));
  throw new Error(formatApiErrorDetail(body.detail, `Request failed (${response.status})`));
}

function isRetryableReviewStatus(status: number): boolean {
  return status === 408 || status === 429 || status >= 500;
}

async function fetchReviewRead(url: string): Promise<Response> {
  const headers = await reviewHeaders();
  let lastError: unknown;

  for (let attempt = 0; attempt <= REVIEW_READ_RETRY_DELAYS_MS.length; attempt++) {
    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), REVIEW_READ_TIMEOUT_MS);
    try {
      const response = await fetch(url, {
        headers,
        cache: 'no-store',
        signal: controller.signal,
      });
      if (!isRetryableReviewStatus(response.status) || attempt === REVIEW_READ_RETRY_DELAYS_MS.length) {
        return response;
      }
    } catch (error) {
      lastError = error instanceof DOMException && error.name === 'AbortError'
        ? new TypeError('Review Queue request timed out')
        : error;
      if (attempt === REVIEW_READ_RETRY_DELAYS_MS.length) throw lastError;
    } finally {
      window.clearTimeout(timeoutId);
    }

    await new Promise(resolve => window.setTimeout(resolve, REVIEW_READ_RETRY_DELAYS_MS[attempt]));
  }

  throw lastError instanceof Error ? lastError : new TypeError('Review Queue request failed');
}

export async function initializeReviewUpload(
  file: File,
  idempotencyKey: string,
  onStage?: (stage: ReviewUploadInitStage) => void,
): Promise<{ id: string; storage_path: string; signed_url: string; upload_token: string; sha256: string }> {
  const fileError = lectureFileError(file);
  if (fileError) throw new UserError(fileError);
  onStage?.('warming_api');
  const warmup = await ensureApiReady();
  if (!warmup.success) {
    throw new Error('Server is temporarily unavailable. Please try again in a moment.');
  }

  onStage?.('reading_file');
  const sha256 = await sha256File(file);
  onStage?.('initializing_upload');
  const response = await fetch(`${API_URL}/api/v2/uploads/init`, {
    method: 'POST',
    headers: await reviewHeaders(),
    body: JSON.stringify({
      filename: file.name,
      mime_type: file.type || 'application/octet-stream',
      size_bytes: file.size,
      sha256,
      idempotency_key: idempotencyKey,
    }),
  });
  return { ...(await parseReviewResponse<{
    id: string;
    storage_path: string;
    signed_url: string;
    upload_token: string;
  }>(response)), sha256 };
}

async function sha256File(file: File): Promise<string> {
  let bytes: ArrayBuffer | null = null;
  let lastError: unknown;

  for (let attempt = 0; attempt <= FILE_READ_RETRY_DELAYS_MS.length; attempt++) {
    try {
      // A fresh slice gives Chromium another file handle after a transient OS
      // read failure without ever accepting partial bytes.
      bytes = await file.slice(0, file.size, file.type).arrayBuffer();
      break;
    } catch (error) {
      lastError = error;
      if (attempt === FILE_READ_RETRY_DELAYS_MS.length) break;
      await new Promise(resolve => setTimeout(resolve, FILE_READ_RETRY_DELAYS_MS[attempt]));
    }
  }

  if (!bytes) {
    const error = new UserError(
      'The browser could not read this file. Please reselect it and try again.',
    ) as UserError & { cause?: unknown };
    error.cause = lastError;
    throw error;
  }

  return sha256Bytes(bytes);
}

export async function uploadReviewSource(
  file: File,
  upload: { storage_path: string; signed_url: string; upload_token: string },
): Promise<void> {
  const supabase = getSupabaseBrowserClient();
  if (supabase) {
    const { error } = await supabase.storage
      .from('review-queue-sources')
      .uploadToSignedUrl(upload.storage_path, upload.upload_token, file, {
        contentType: file.type || 'application/octet-stream',
      });
    if (error) throw new Error(error.message);
    return;
  }

  const response = await fetch(upload.signed_url, {
    method: 'PUT',
    headers: { 'Content-Type': file.type || 'application/octet-stream' },
    body: file,
  });
  if (!response.ok) throw new Error('Could not upload source file');
}

export async function completeReviewUpload(
  uploadId: string,
  sha256?: string,
): Promise<void> {
  const response = await fetch(`${API_URL}/api/v2/uploads/${uploadId}/complete`, {
    method: 'POST',
    headers: await reviewHeaders(),
    body: JSON.stringify({ sha256 }),
  });
  await parseReviewResponse(response);
}

export async function createReviewGenerationJob(
  uploadId: string,
  options: {
    depth: 'concise' | 'balanced' | 'thorough';
    cardTypes: FlashCard['type'][];
    acceptedGoal: number;
    idempotencyKey: string;
    courseId?: string;
    coursePosition?: number;
  },
): Promise<ReviewGenerationJob> {
  const response = await fetch(`${API_URL}/api/v2/generation-jobs`, {
    method: 'POST',
    headers: await reviewHeaders(),
    body: JSON.stringify({
      upload_id: uploadId,
      requested_depth: options.depth,
      requested_card_types: options.cardTypes,
      accepted_goal: options.acceptedGoal,
      course_id: options.courseId,
      course_position: options.coursePosition,
      idempotency_key: options.idempotencyKey,
    }),
  });
  return parseReviewResponse(response);
}

export async function getReviewSourceFile(jobId: string, filename: string): Promise<File> {
  const response = await fetchReviewRead(`${API_URL}/api/v2/generation-jobs/${jobId}/source`);
  if (!response.ok) throw new Error('Could not retrieve the original lecture. Please retry saving the complete deck.');
  const type = filename.toLowerCase().endsWith('.pdf') ? 'application/pdf'
    : filename.toLowerCase().endsWith('.pptx') ? 'application/vnd.openxmlformats-officedocument.presentationml.presentation' : 'application/vnd.ms-powerpoint';
  return new File([await response.blob()], filename, { type });
}

export async function getReviewGenerationJob(
  jobId: string,
): Promise<ReviewGenerationJob> {
  const response = await fetchReviewRead(`${API_URL}/api/v2/generation-jobs/${jobId}`);
  return parseReviewResponse(response);
}

export async function listReviewCandidates(
  jobId: string,
  status: 'pending' | 'accepted' | 'rejected' | 'incorrect' = 'pending',
): Promise<ReviewCandidate[]> {
  const candidates: ReviewCandidate[] = [];
  let cursor: ReviewCandidate | undefined;
  while (true) {
    const params = new URLSearchParams({ status, limit: '200' });
    if (cursor) {
      params.set('after_ordinal', String(cursor.ordinal));
      params.set('after_id', cursor.id);
    }
    const response = await fetchReviewRead(`${API_URL}/api/v2/generation-jobs/${jobId}/candidates?${params}`);
    const result = await parseReviewResponse<{ candidates: ReviewCandidate[] }>(response);
    const page = result.candidates;
    if (cursor && page.length && (
      page[0].ordinal < cursor.ordinal
      || (page[0].ordinal === cursor.ordinal && page[0].id <= cursor.id)
    )) throw new Error('Could not load all saved cards. Please try again.');
    candidates.push(...page);
    if (page.length < 200) return candidates;
    cursor = page[page.length - 1];
  }
}

export async function getReviewQueueSnapshot(
  jobId: string,
): Promise<ReviewQueueSnapshot> {
  const params = new URLSearchParams({ candidate_status: 'pending', limit: '200' });
  const response = await fetchReviewRead(`${API_URL}/api/v2/generation-jobs/${jobId}/snapshot?${params}`);

  // Keep the frontend deployable before the companion API release reaches
  // production. FastAPI's route-level 404 is exactly "Not Found"; a missing
  // job uses the repository's distinct job_not_found detail and must surface.
  if (response.status === 404) {
    const body = await response.clone().json().catch(() => null);
    if (body?.detail === 'Not Found') {
      const job = await getReviewGenerationJob(jobId);
      const candidates = await listReviewCandidates(jobId, 'pending');
      return { job, candidates };
    }
  }

  return parseReviewResponse<ReviewQueueSnapshot>(response);
}

export async function getReviewCoverage(jobId: string): Promise<ReviewCoverage> {
  const response = await fetchReviewRead(`${API_URL}/api/v2/generation-jobs/${jobId}/coverage`);
  return parseReviewResponse(response);
}

export async function getCardPreferences(): Promise<CardPreferences> {
  const response = await fetchReviewRead(`${API_URL}/api/v2/preferences`);
  return parseReviewResponse(response);
}

export async function updateCardPreferences(preferences: Pick<
  CardPreferences,
  'preferred_depth' | 'preferred_card_types'
>): Promise<CardPreferences> {
  const response = await fetch(`${API_URL}/api/v2/preferences`, {
    method: 'PATCH',
    headers: await reviewHeaders(),
    body: JSON.stringify(preferences),
  });
  return parseReviewResponse(response);
}

export async function createStudyCourse(name: string): Promise<StudyCourse> {
  const response = await fetch(`${API_URL}/api/v2/courses`, {
    method: 'POST',
    headers: await reviewHeaders(),
    body: JSON.stringify({ name }),
  });
  return parseReviewResponse(response);
}

export async function listStudyCourses(): Promise<StudyCourse[]> {
  const response = await fetchReviewRead(`${API_URL}/api/v2/courses`);
  return parseReviewResponse(response);
}

export type CourseSummary = Omit<StudyCourse, 'lectures'> & { lecture_count: number };
export type CourseCatalog = { courses: CourseSummary[]; page: number; has_next: boolean };
export async function listCourseCatalog(query: string, sort: string, page: number): Promise<CourseCatalog> {
  const params = new URLSearchParams({ q: query, sort, page: String(page) });
  return parseReviewResponse(await fetchReviewRead(`${API_URL}/api/v2/courses/catalog?${params}`));
}

export async function getStudyCourse(courseId: string): Promise<StudyCourse> {
  const response = await fetchReviewRead(`${API_URL}/api/v2/courses/${courseId}`);
  return parseReviewResponse(response);
}

export async function reviewCandidate(
  candidateId: string,
  decision: {
    action: ReviewDecision['action'];
    selectedVersionId?: string | null;
    rejectionReason?: RejectionReason | null;
    presentedAt: string;
    decisionLatencyMs: number;
    clientSequence: number;
    idempotencyKey: string;
  },
): Promise<ReviewDecision> {
  const response = await fetch(`${API_URL}/api/v2/candidates/${candidateId}/reviews`, {
    method: 'POST',
    headers: await reviewHeaders(),
    body: JSON.stringify({
      selected_version_id: decision.selectedVersionId,
      action: decision.action,
      rejection_reason: decision.rejectionReason,
      presented_at: decision.presentedAt,
      decision_latency_ms: decision.decisionLatencyMs,
      client_sequence: decision.clientSequence,
      idempotency_key: decision.idempotencyKey,
    }),
  });
  return parseReviewResponse(response);
}

export async function updateReviewReason(
  reviewId: string,
  rejectionReason: RejectionReason,
  reasonLatencyMs: number,
): Promise<ReviewDecision> {
  const response = await fetch(`${API_URL}/api/v2/reviews/${reviewId}/reason`, {
    method: 'PATCH',
    headers: await reviewHeaders(),
    body: JSON.stringify({
      rejection_reason: rejectionReason,
      reason_latency_ms: reasonLatencyMs,
    }),
  });
  return parseReviewResponse(response);
}

export async function createReviewCardVersion(
  candidate: ReviewCandidate,
  content: Partial<FlashCard>,
  editDiff: Record<string, unknown>,
  normalizedEditDistance?: number,
): Promise<ReviewCardVersion> {
  const response = await fetch(`${API_URL}/api/v2/candidates/${candidate.id}/versions`, {
    method: 'POST',
    headers: await reviewHeaders(),
    body: JSON.stringify({
      front: content.front,
      back: content.back,
      cloze_text: content.clozeText === undefined ? undefined : normalizeClozeText(content.clozeText),
      image_ref: content.image,
      occluded_image_ref: content.occludedImage,
      answer: content.answer,
      hint: content.hint,
      tags: content.tags || [],
      edit_diff: editDiff,
      normalized_edit_distance: normalizedEditDistance,
    }),
  });
  return parseReviewResponse(response);
}

export async function undoReviewDecision(
  reviewId: string,
): Promise<ReviewDecision> {
  const response = await fetch(`${API_URL}/api/v2/reviews/${reviewId}/undo`, {
    method: 'POST',
    headers: await reviewHeaders(),
    body: JSON.stringify({}),
  });
  return parseReviewResponse(response);
}

export async function cancelReviewGenerationJob(
  jobId: string,
): Promise<ReviewGenerationJob> {
  const response = await fetch(`${API_URL}/api/v2/generation-jobs/${jobId}/cancel`, {
    method: 'POST',
    headers: await reviewHeaders(),
    body: JSON.stringify({}),
  });
  return parseReviewResponse(response);
}

export function reviewCandidateToFlashCard(candidate: ReviewCandidate): FlashCard {
  const version = candidate.current_version;
  return {
    id: candidate.id,
    type: candidate.card_type,
    front: version.front || undefined,
    back: version.back || undefined,
    clozeText: version.cloze_text || undefined,
    image: version.image_ref,
    occludedImage: version.occluded_image_ref,
    answer: version.answer,
    hint: version.hint,
    tags: version.tags || [],
  };
}

export async function uploadFile(file: File): Promise<UploadResponse> {
  // Mock mode — return fake upload response without hitting the API
  if (MOCK_GENERATION_ENABLED) {
    console.log('[Upload] MOCK mode — skipping real upload');
    await new Promise(r => setTimeout(r, 800));
    return {
      filename: file.name,
      num_slides: 12,
      slides: Array.from({ length: 12 }, (_, i) => ({
        slide_number: i + 1,
        text: `Mock slide ${i + 1} content about cardiac physiology and electrophysiology.`,
        images: [],
      })),
      full_text: 'Mock extracted text from lecture slides about cardiac physiology, electrophysiology, and heart failure management.',
      session_id: `mock-session-${Date.now()}`,
    };
  }

  const uploadStartTime = Date.now();

  // Step 1: Ensure API is ready (handles cold start silently)
  const warmupResult = await ensureApiReady();

  if (!warmupResult.success) {
    logColdStartMetric({
      event: 'upload_blocked_by_warmup',
      durationMs: warmupResult.durationMs,
      attempts: warmupResult.attempts,
      wasColdStart: true,
      error: 'Warmup failed before upload',
    });
    throw new Error('Server is temporarily unavailable. Please try again in a moment.');
  }

  // Step 2: Upload with retry logic
  const formData = new FormData();
  formData.append('file', file);

  const doUpload = async (attempt: number): Promise<Response> => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), UPLOAD_TIMEOUT_MS);

    try {
      const res = await fetch(`${API_URL}/api/upload`, {
        method: 'POST',
        headers: await authHeaders(),
        body: formData,
        signal: controller.signal,
      });
      clearTimeout(timeoutId);
      return res;
    } catch (err) {
      clearTimeout(timeoutId);
      throw err;
    }
  };

  let lastError: Error | null = null;

  for (let attempt = 1; attempt <= MAX_UPLOAD_RETRIES; attempt++) {
    const attemptStartTime = Date.now();

    try {
      console.log(`[API] Upload attempt ${attempt}/${MAX_UPLOAD_RETRIES}...`);
      const res = await doUpload(attempt);

      if (res.ok) {
        const totalDuration = Date.now() - uploadStartTime;
        console.log(`[API] Upload succeeded on attempt ${attempt} (${totalDuration}ms total)`);

        // Log success metrics
        logColdStartMetric({
          event: 'upload_success',
          durationMs: totalDuration,
          attempts: attempt,
          wasColdStart: warmupResult.wasColdStart,
        });

        return res.json();
      }

      // HTTP error - parse and throw
      const errBody = await res.json().catch(() => ({ detail: 'Upload failed' }));
      const errMsg = errBody.detail || `Upload failed (${res.status})`;

      // Don't retry client errors (4xx) except 408 (timeout) and 429 (rate limit)
      if (res.status >= 400 && res.status < 500 && res.status !== 408 && res.status !== 429) {
        console.error(`[API] Upload failed with client error ${res.status}: ${errMsg}`);
        logColdStartMetric({
          event: 'upload_client_error',
          durationMs: Date.now() - uploadStartTime,
          attempts: attempt,
          wasColdStart: warmupResult.wasColdStart,
          error: errMsg,
        });
        throw new UserError(errMsg);
      }

      lastError = new Error(errMsg);
      console.warn(`[API] Upload attempt ${attempt} failed with ${res.status}: ${errMsg}`);
    } catch (err) {
      // Re-throw UserErrors immediately — don't retry user-caused issues
      if (err instanceof UserError) throw err;
      const attemptDuration = Date.now() - attemptStartTime;
      const isTimeout = err instanceof Error && err.name === 'AbortError';
      const errorMsg = isTimeout ? 'Request timed out' : (err instanceof Error ? err.message : 'Unknown error');

      console.warn(`[API] Upload attempt ${attempt} failed after ${attemptDuration}ms: ${errorMsg}`);
      lastError = new Error(errorMsg);

      // Log timeout specifically
      if (isTimeout) {
        logColdStartMetric({
          event: 'upload_timeout',
          durationMs: attemptDuration,
          attempts: attempt,
          wasColdStart: warmupResult.wasColdStart,
          error: 'Request timed out',
        });
      }
    }

    // Wait before retry (exponential backoff)
    if (attempt < MAX_UPLOAD_RETRIES) {
      const delay = RETRY_DELAYS_MS[attempt - 1] || RETRY_DELAYS_MS[RETRY_DELAYS_MS.length - 1];
      console.log(`[API] Waiting ${delay}ms before retry...`);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }

  // All retries exhausted
  const totalDuration = Date.now() - uploadStartTime;
  console.error(`[API] Upload failed after ${MAX_UPLOAD_RETRIES} attempts (${totalDuration}ms total)`);

  logColdStartMetric({
    event: 'upload_failed_all_retries',
    durationMs: totalDuration,
    attempts: MAX_UPLOAD_RETRIES,
    wasColdStart: warmupResult.wasColdStart,
    error: lastError?.message || 'All retries exhausted',
  });

  throw lastError || new Error('Upload failed after multiple attempts. Please check your connection and try again.');
}

export async function generateCards(
  text: string,
  options?: { card_types?: string[]; max_cards?: number },
  sessionId?: string | null,
  slideImages?: Record<string, string[]>,
): Promise<GenerateResponse> {
  const body: Record<string, unknown> = {
    text,
    options: options || { card_types: ['cloze', 'basic'] },  // no max_cards = backend auto-detects
  };

  if (sessionId) {
    body.session_id = sessionId;
  }
  if (slideImages && Object.keys(slideImages).length > 0) {
    body.slide_images = slideImages;
  }

  const res = await fetch(`${API_URL}/api/generate`, {
    method: 'POST',
    headers: await authHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(body),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: 'Generation failed' }));
    throw new Error(err.detail || `Generation failed (${res.status})`);
  }

  const result: GenerateResponse = await res.json();
  if (!result.cards.length) {
    throw new Error('The server completed generation without producing any cards');
  }
  return result;
}

/**
 * WebSocket message types from the server
 */
interface WSMessage {
  type: 'connected' | 'progress' | 'card' | 'complete' | 'error';
  stage?: string;
  message?: string;
  progress?: number;
  index?: number;
  card?: FlashCard;
  total_cards?: number;
  model_used?: string;
}

/**
 * Result of starting a streaming generation.
 */
export interface StreamingGeneration {
  /** Promise that resolves when generation completes */
  promise: Promise<{ total_cards: number; model_used: string }>;
  /** Call to cancel the generation (closes WebSocket) */
  cancel: () => void;
}

/**
 * Stream card generation via WebSocket.
 * Cards are delivered progressively via the onCard callback.
 * Returns an object with the promise and a cancel function.
 */
export function generateCardsStreaming(
  text: string,
  options?: { card_types?: string[]; max_cards?: number; include_image_occlusion?: boolean; blackout_other_labels?: boolean },
  sessionId?: string | null,
  slideImages?: Record<string, string[]>,
  onCard?: (card: FlashCard, index: number) => void,
  onProgress?: (stage: string, message: string, progress?: number) => void,
): StreamingGeneration {
  // Mock mode for local testing — no API credits used
  if (MOCK_GENERATION_ENABLED) {
    console.log('[Generate] MOCK mode enabled — returning fake cards');
    return generateCardsMock(onCard, onProgress);
  }

  const wsUrl = API_URL.replace(/^http/, 'ws') + '/api/ws/generate';
  const ws = new WebSocket(wsUrl);
  
  let resolved = false;
  let cancelled = false;
  let rejectPromise: ((reason?: unknown) => void) | null = null;
  
  const promise = new Promise<{ total_cards: number; model_used: string }>((resolve, reject) => {
    rejectPromise = reject;
    ws.onopen = async () => {
      let authorization: string;
      try {
        authorization = (await authHeaders()).Authorization;
      } catch (error) {
        resolved = true;
        reject(error);
        ws.close();
        return;
      }
      if (cancelled || resolved) return;
      const body: Record<string, unknown> = {
        access_token: authorization.slice('Bearer '.length),
        text,
        options: {
          card_types: options?.card_types || ['cloze', 'basic'],
          include_image_occlusion: options?.include_image_occlusion ?? true,
          blackout_other_labels: options?.blackout_other_labels ?? true,
        },
      };

      if (options?.max_cards) {
        (body.options as Record<string, unknown>).max_cards = options.max_cards;
      }
      if (sessionId) {
        body.session_id = sessionId;
      }
      if (slideImages && Object.keys(slideImages).length > 0) {
        body.slide_images = slideImages;
      }
      if (typeof window !== 'undefined') {
        const deviceId = localStorage.getItem('slidetoanki_device_id');
        if (deviceId) {
          body.device_id = deviceId;
        }
      }

      ws.send(JSON.stringify(body));
    };
    
    ws.onmessage = (event) => {
      try {
        const msg: WSMessage = JSON.parse(event.data);
        
        switch (msg.type) {
          case 'connected':
            break;
          case 'progress':
            onProgress?.(msg.stage || '', msg.message || '', msg.progress);
            break;
          case 'card':
            if (msg.card && msg.index !== undefined) {
              onCard?.(msg.card, msg.index);
            }
            break;
          case 'complete':
            resolved = true;
            ws.close();
            resolve({ total_cards: msg.total_cards || 0, model_used: msg.model_used || 'unknown' });
            break;
          case 'error':
            resolved = true;
            ws.close();
            reject(new Error(msg.message || 'Unknown error'));
            break;
        }
      } catch (e) {
        console.error('[WS] Failed to parse message:', e);
      }
    };
    
    ws.onerror = () => {
      if (!resolved && !cancelled) {
        resolved = true;
        reject(new Error('WebSocket connection failed'));
      }
    };
    
    ws.onclose = () => {
      if (!resolved && !cancelled) {
        resolved = true;
        reject(new Error('WebSocket closed unexpectedly'));
      }
    };
  });
  
  const cancel = () => {
    if (!resolved && !cancelled) {
      cancelled = true;
      resolved = true;
      ws.close();
      rejectPromise?.(new GenerationCancelledError());
    }
  };
  
  return { promise, cancel };
}

// ── Mock generation for local testing (NEXT_PUBLIC_MOCK_GENERATION=true) ──

const MOCK_CARDS: FlashCard[] = [
  {
    id: 'mock-1',
    type: 'cloze',
    clozeText: 'The {{c1::sinoatrial (SA) node}} generates impulses at 60–100 bpm and is the primary pacemaker of the heart.',
    tags: ['cardiology', 'electrophysiology'],
  },
  {
    id: 'mock-2',
    type: 'basic',
    front: 'Why does the plateau phase of the cardiac action potential prevent tetanic contraction?',
    back: 'The prolonged plateau (phase 2) keeps the membrane depolarized long enough for the refractory period to outlast mechanical contraction, preventing summation of twitches.',
    tags: ['cardiology', 'electrophysiology'],
  },
  {
    id: 'mock-3',
    type: 'cloze',
    clozeText: 'Ejection fraction (EF) = {{c1::SV/EDV}}. Normal EF is > 55%.',
    tags: ['cardiology', 'heart-failure'],
  },
  {
    id: 'mock-4',
    type: 'basic',
    front: 'What is the mechanism of action of beta-blockers in heart failure?',
    back: 'Beta-blockers reduce heart rate and myocardial oxygen demand, decrease sympathetic activation, and prevent adverse cardiac remodeling over time.',
    tags: ['cardiology', 'pharmacology'],
  },
  {
    id: 'mock-5',
    type: 'cloze',
    clozeText: 'The {{c1::Frank-Starling mechanism}} states that increased preload leads to increased stroke volume up to a physiological limit.',
    tags: ['cardiology', 'physiology'],
  },
  {
    id: 'mock-6',
    type: 'basic',
    front: 'Differentiate between HFrEF and HFpEF.',
    back: 'HFrEF (EF < 40%) involves systolic dysfunction with impaired contractility. HFpEF (EF ≥ 50%) involves diastolic dysfunction with impaired relaxation and filling.',
    tags: ['cardiology', 'heart-failure'],
  },
  {
    id: 'mock-7',
    type: 'cloze',
    clozeText: 'Hyperkalemia causes {{c1::peaked T waves}} on ECG because elevated K⁺ accelerates phase 3 repolarization.',
    tags: ['cardiology', 'electrolytes'],
  },
  {
    id: 'mock-8',
    type: 'cloze',
    clozeText: 'Cardiac output = {{c1::heart rate}} × stroke volume. Normal resting CO is approximately 5 L/min.',
    tags: ['cardiology', 'physiology'],
  },
];

function generateCardsMock(
  onCard?: (card: FlashCard, index: number) => void,
  onProgress?: (stage: string, message: string, progress?: number) => void,
): StreamingGeneration {
  let cancelled = false;

  async function run(): Promise<{ total_cards: number; model_used: string }> {
    onProgress?.('analyzing', 'Analyzing slides (mock)...', 0);
    await new Promise(r => setTimeout(r, 500));

    onProgress?.('generating', 'Generating cards (mock)...', 10);

    for (let i = 0; i < MOCK_CARDS.length; i++) {
      if (cancelled) break;
      await new Promise(r => setTimeout(r, 300 + Math.random() * 400));
      onCard?.({ ...MOCK_CARDS[i], id: `mock-${Date.now()}-${i}` }, i);
      onProgress?.('generating', `Generated card ${i + 1}/${MOCK_CARDS.length}`, ((i + 1) / MOCK_CARDS.length) * 100);
    }

    return { total_cards: MOCK_CARDS.length, model_used: 'mock' };
  }

  return {
    promise: run(),
    cancel: () => { cancelled = true; },
  };
}

/** Sanitize a tag for Anki: replace spaces/quotes/backslashes with underscores, collapse and trim. */
function sanitizeTag(tag: string): string {
  return tag.replace(/[\s"\\]/g, '_').replace(/_+/g, '_').replace(/(^_|_$)/g, '');
}

export async function exportToAnki(
  cards: FlashCard[],
  deckName: string = 'SlideToAnki Export',
  sessionId?: string | null,
  onProgress?: ReportExportProgress,
): Promise<Blob> {
  onProgress?.({ stage: 'preparing', cardCount: cards.length });
  // Users often edit cards for several minutes before exporting, long enough
  // for the API container to sleep again. Wake it before the export request.
  const warmupResult = await ensureApiReady();
  if (!warmupResult.success) {
    throw new Error('Server is temporarily unavailable. Please try exporting again in a moment.');
  }

  const body: Record<string, unknown> = {
    cards: cards.map(({ liked, ...card }) => ({
      ...card,
      tags: card.tags.map(sanitizeTag),
    })),
    deck_name: deckName,
  };

  if (sessionId) {
    body.session_id = sessionId;
  }

  const payload = JSON.stringify(body);

  const doExport = async (): Promise<{ res: Response; blob?: Blob }> => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), EXPORT_TIMEOUT_MS);

    try {
      onProgress?.({ stage: 'building' });
      const res = await fetch(`${API_URL}/api/export`, {
        method: 'POST',
        headers: await authHeaders({ 'Content-Type': 'application/json' }),
        body: payload,
        signal: controller.signal,
      });
      if (res.ok) {
        onProgress?.({ stage: 'downloading' });
        return { res, blob: await res.blob() };
      }
      return { res };
    } finally {
      clearTimeout(timeoutId);
    }
  };

  let lastError: Error | null = null;

  for (let attempt = 1; attempt <= MAX_EXPORT_RETRIES; attempt++) {
    try {
      console.log(`[API] Export attempt ${attempt}/${MAX_EXPORT_RETRIES}...`);
      const { res, blob } = await doExport();

      if (res.ok) {
        console.log(`[API] Export succeeded on attempt ${attempt}`);
        return blob!;
      }

      const errBody = await res.json().catch(() => ({ detail: 'Export failed' }));
      const errMsg = formatApiErrorDetail(errBody.detail, `Export failed (${res.status})`);

      // Don't retry client errors (4xx) except 408/429
      if (res.status >= 400 && res.status < 500 && res.status !== 408 && res.status !== 429) {
        throw new UserError(errMsg);
      }

      lastError = new Error(errMsg);
      console.warn(`[API] Export attempt ${attempt} failed with ${res.status}: ${errMsg}`);
    } catch (err) {
      if (err instanceof UserError) throw err;
      const isTimeout = err instanceof Error && err.name === 'AbortError';
      const errorMsg = isTimeout ? 'Export timed out' : (err instanceof Error ? err.message : 'Unknown error');
      lastError = new Error(errorMsg);
      console.warn(`[API] Export attempt ${attempt} failed: ${errorMsg}`);
    }

    // Wait before retry (exponential backoff)
    if (attempt < MAX_EXPORT_RETRIES) {
      onProgress?.({ stage: 'retrying' });
      const delay = RETRY_DELAYS_MS[attempt - 1] || RETRY_DELAYS_MS[RETRY_DELAYS_MS.length - 1];
      console.log(`[API] Waiting ${delay}ms before export retry...`);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }

  throw lastError || new Error('Export failed after multiple attempts. Please check your connection and try again.');
}

export interface FeedbackRequest {
  device_id: string;
  cards_generated?: number;
  good_per_ten: number;
  needs_edit_per_ten: number;
  feedback_text?: string;
  session_id?: string;
  model_used?: string;
}

export interface FeedbackResponse {
  success: boolean;
  message: string;
}

export async function submitFeedback(feedback: FeedbackRequest): Promise<FeedbackResponse> {
  const res = await fetch(`${API_URL}/api/feedback`, {
    method: 'POST',
    headers: await authHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(feedback),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: 'Feedback submission failed' }));
    throw new Error(err.detail || `Feedback failed (${res.status})`);
  }

  return res.json();
}
